import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import './WorkshopsCertificates.css';

function WorkshopsCertificates() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [workshop, setWorkshop] = useState(null);
  const [loading, setLoading] = useState(false);
  const [moderatorRate, setModeratorRate] = useState(0);
  const [contentRate, setContentRate] = useState(0);
  const [email, setEmail] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [userStats, setUserStats] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [resultData, setResultData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const baseURL = useMemo(() => `${process.env.REACT_APP_BASE_URL}crm/`, []);

  const fetchWorkshop = async () => {
    if (!token) {
      setWorkshop(null);
      return;
    }
    setLoading(true);
    try {
      const data = new FormData();
      data.append('event_token', token);
      const response = await fetch(`${baseURL}get/get_workshop_by_token.php`, {
        method: 'POST',
        body: data
      });
      const text = await response.text();
      if (text === 'NOT_FOUND' || text === 'MISSING_PARAMS') {
        setWorkshop(null);
        return;
      }
      const parsed = JSON.parse(text);
      setWorkshop(parsed);
    } catch (error) {
      console.error('Error cargando workshop:', error);
      setWorkshop(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkshop();
  }, [token]);

  const formatDate = (dateValue) => {
    if (!dateValue) {
      return '';
    }
    const parsed = new Date(dateValue.replace(' ', 'T'));
    if (Number.isNaN(parsed.getTime())) {
      return dateValue;
    }
    return parsed.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const handleDownload = async (event) => {
    event.preventDefault();
    setStatusMessage('');
    setShowResult(false);
    setResultData(null);

    if (!workshop || !moderatorRate || !contentRate || !email) {
      setStatusMessage('Completa todos los campos antes de continuar.');
      return;
    }

    setIsSubmitting(true);

    try {
      const coords = {
        name: { x: 270, y: 250, size: 30 },
        workshop_name: { x: 270, y: 290, size: 18 },
        date: { x: 270, y: 310, size: 14 },
        email: { x: 0, y: 535, size: 8 },
        qr: { x: 10, y: 510, size: 60 },
        qr_label: { x: 10, y: 500, size: 10 }
      };

      const data = new FormData();
      data.append('workshop_id', workshop.id);
      data.append('event_token', token);
      data.append('moderator_rate', moderatorRate);
      data.append('content_rate', contentRate);
      data.append('voter_email', email);
      // voter_name se obtendrá del API si está disponible, sino se usará el email
      data.append('voter_name', email);
      data.append('coords', JSON.stringify(coords));
      
      // Agregar token si está disponible (para obtener estadísticas del usuario)
      const sessionToken = localStorage.getItem('token');
      if (sessionToken) {
        data.append('token', sessionToken);
      }

      const response = await fetch(`${baseURL}set/get_workshop_cert.php`, {
        method: 'POST',
        body: data
      });
      
      // Log completo de la respuesta
      console.log('🔍 ===== RESPUESTA COMPLETA DE get_workshop_certQA.php =====');
      console.log('🔍 Status:', response.status, response.statusText);
      console.log('🔍 Headers completos:');
      response.headers.forEach((value, key) => {
        console.log(`🔍   ${key}: ${value}`);
      });
      console.log('🔍 Content-Type:', response.headers.get('content-type'));
      console.log('🔍 X-User-Stats presente:', response.headers.get('X-User-Stats') ? 'SÍ' : 'NO');
      console.log('🔍 X-Certificate-Message presente:', response.headers.get('X-Certificate-Message') ? 'SÍ' : 'NO');
      
      // Leer mensaje del certificado del header
      const certMessageHeader = response.headers.get('X-Certificate-Message');
      let successMessage = null;
      if (certMessageHeader) {
        try {
          successMessage = atob(certMessageHeader);
          console.log('🔍 X-Certificate-Message decodificado:', successMessage);
        } catch (error) {
          console.error('❌ Error decodificando mensaje del certificado:', error);
        }
      }
      
      // Leer estadísticas del usuario del header antes de convertir a blob
      const userStatsHeader = response.headers.get('X-User-Stats');
      console.log('🔍 X-User-Stats header recibido:', userStatsHeader ? 'presente' : 'ausente');
      if (userStatsHeader) {
        console.log('🔍 X-User-Stats raw (primeros 200 chars):', userStatsHeader.substring(0, 200));
      }
      
      if (userStatsHeader) {
        try {
          const decodedStats = JSON.parse(atob(userStatsHeader));
          console.log('🔍 Estadísticas decodificadas:', decodedStats);
          setUserStats(decodedStats);
        } catch (error) {
          console.error('❌ Error decodificando estadísticas del usuario:', error);
        }
      }
      
      const contentType = response.headers.get('content-type') || '';
      console.log('🔍 Content-Type detectado:', contentType);
      
      if (!contentType.includes('application/pdf')) {
        // Si no es PDF, puede ser un error JSON o texto
        const errorText = await response.text();
        console.log('🔍 Respuesta NO es PDF. Contenido recibido:', errorText);
        
        let resultMessage = '';
        let resultType = 'error'; // 'success', 'error', 'warning'
        
        try {
          // Intentar parsear como JSON
          const errorData = JSON.parse(errorText);
          console.log('🔍 Error parseado como JSON:', errorData);
          
          if (errorData.error === 'CERTIFICATE_REQUIREMENTS_NOT_MET' && errorData.message) {
            resultMessage = errorData.message;
            resultType = 'warning';
          } else if (errorData.error === 'USER_NOT_FOUND' && errorData.message) {
            resultMessage = errorData.message;
            resultType = 'error';
          } else {
            resultMessage = errorData.message || 'Ocurrió un error al generar el certificado.';
            resultType = 'error';
          }
        } catch (jsonError) {
          console.log('🔍 No es JSON válido, tratando como texto');
          // Si no es JSON, tratar como texto
          if (errorText === 'FPDI_NOT_INSTALLED') {
            resultMessage = 'El servidor no tiene la librería FPDI instalada.';
          } else if (errorText === 'QRCODE_NOT_INSTALLED') {
            resultMessage = 'El servidor no tiene la librería para generar QR.';
          } else if (errorText === 'TEMPLATE_NOT_FOUND') {
            resultMessage = 'No se encontró la plantilla del certificado.';
          } else {
            resultMessage = errorText || 'Ocurrió un error al generar el certificado.';
          }
          resultType = 'error';
        }
        
        // Mostrar pantalla de resultado
        setResultData({
          type: resultType,
          message: resultMessage,
          hasCertificate: false
        });
        setShowResult(true);
        setIsSubmitting(false);
        console.log('🔍 ===== FIN DE RESPUESTA (ERROR) =====');
        return;
      }
      
      // Si llegamos aquí, el PDF se generó correctamente
      console.log('🔍 Respuesta es PDF, convirtiendo a blob...');
      const blob = await response.blob();
      console.log('🔍 Blob creado. Tamaño:', blob.size, 'bytes');
      console.log('🔍 ===== FIN DE RESPUESTA (PDF) =====');
      
      // Descargar el PDF
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `certificado-${workshop.name || 'workshop'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      // Mostrar pantalla de resultado con éxito
      setResultData({
        type: 'success',
        message: successMessage || 'Tu votación ha sido registrada con éxito y tu certificado ha sido descargado.',
        hasCertificate: true
      });
      setShowResult(true);
      setIsSubmitting(false);
    } catch (error) {
      console.error('Error descargando certificado:', error);
      setResultData({
        type: 'error',
        message: 'Ocurrió un error al procesar tu solicitud. Por favor, intenta nuevamente.',
        hasCertificate: false
      });
      setShowResult(true);
      setIsSubmitting(false);
    }
  };

  const renderStars = (value, onChange) => (
    <div className="rating-stars">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          className={`star ${star <= value ? 'active' : ''}`}
          onClick={() => onChange(star)}
          aria-label={`Calificar ${star} estrellas`}
        >
          ★
        </button>
      ))}
    </div>
  );

  return (
    <div className="workshop-cert-page">
      <div className="workshop-card">
        {loading ? (
          <div className="text-muted">Cargando...</div>
        ) : !workshop ? (
          <div className="text-muted">No se encontró el taller solicitado.</div>
        ) : (
          <>
            <h1 className="workshop-title">{workshop.name}</h1>
            <div className="workshop-date">{formatDate(workshop.event_date)}</div>
            {workshop.event_link && (
              <a className="workshop-link" href={workshop.event_link} target="_blank" rel="noreferrer">
                ▶ Ver en YouTube
              </a>
            )}

            {!showResult && (
              <>
                <div className="rating-block">
                  <h3>Calidad del moderador</h3>
                  {renderStars(moderatorRate, setModeratorRate)}
                </div>

                <div className="rating-block">
                  <h3>Eficiencia del contenido</h3>
                  {renderStars(contentRate, setContentRate)}
                </div>
              </>
            )}

            {!showResult ? (
              <>
                <form className="certificate-form" onSubmit={handleDownload}>
                  <div style={{ marginBottom: '15px' }}>
                    <label htmlFor="email-input" style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                      Correo electrónico
                    </label>
                    <input
                      id="email-input"
                      type="email"
                      className="form-control"
                      placeholder="tu@email.com"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      required
                      disabled={isSubmitting}
                    />
                    <small style={{ display: 'block', marginTop: '5px', color: '#666', fontSize: '13px' }}>
                      Introduzca el correo electrónico con el que tiene su cuenta activa en <a href="https://TOMi.digital" target="_blank" rel="noreferrer" style={{ color: '#007bff' }}>https://TOMi.digital</a>
                    </small>
                  </div>
                  <button 
                    type="submit" 
                    className="btn btn-outline-dark w-100"
                    disabled={isSubmitting || !email || !moderatorRate || !contentRate}
                  >
                    {isSubmitting ? 'Procesando...' : 'Enviar Votación'}
                  </button>
                </form>

                {statusMessage && <div className="status-message">{statusMessage}</div>}
              </>
            ) : (
              <div className="result-screen" style={{ padding: '20px', textAlign: 'center' }}>
                <div style={{ 
                  fontSize: '48px', 
                  marginBottom: '20px',
                  color: resultData?.type === 'success' ? '#28a745' : resultData?.type === 'warning' ? '#ffc107' : '#dc3545'
                }}>
                  {resultData?.type === 'success' ? '✓' : resultData?.type === 'warning' ? '⚠' : '✗'}
                </div>
                <h2 style={{ marginBottom: '15px', fontSize: '24px', fontWeight: 'bold' }}>
                  {resultData?.type === 'success' ? '¡Votación Registrada!' : 'Votación Registrada'}
                </h2>
                <p style={{ 
                  fontSize: '16px', 
                  marginBottom: '20px',
                  color: resultData?.type === 'success' ? '#28a745' : resultData?.type === 'warning' ? '#856404' : '#dc3545',
                  lineHeight: '1.6'
                }}>
                  {resultData?.message || 'Tu votación ha sido registrada con éxito.'}
                </p>
                {resultData?.hasCertificate && (
                  <p style={{ fontSize: '14px', color: '#666', marginTop: '10px' }}>
                    Tu certificado ha sido descargado automáticamente.
                  </p>
                )}
                <button
                  type="button"
                  className="btn btn-outline-dark"
                  onClick={() => {
                    setShowResult(false);
                    setResultData(null);
                    setEmail('');
                    setModeratorRate(0);
                    setContentRate(0);
                    setStatusMessage('');
                    setUserStats(null);
                  }}
                  style={{ marginTop: '20px' }}
                >
                  Volver
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default WorkshopsCertificates;
